import type {
  ReactElement,
  ReactNode,
} from 'react';

import styled, { useTheme } from 'styled-components';

import { Cell } from '../Cell';
import type {
  CellValue,
} from '../Cell';
import { Hint } from '../Hint';
import { Row } from '../Row';

import { FieldWrapper } from '../FieldWrapper';

export const StyledWrapper = styled.div<{
  $isShow: boolean;
  $hasRepeatOffsets: boolean;
}>(({
  $isShow,

  theme: {
    indents,
  },
}) => ({
  '& + &': {
    marginTop: $isShow ? indents.INDENT_2 : indents.INDENT_3,
  },
}));

export type StyledLabelBlockProps = {
  $isInput: boolean;
  $hasRepeatOffsets: boolean;
  $direction: FieldGroupDirectionType;
};

export const StyledLabelBlock = styled.div<StyledLabelBlockProps>(({
  $isInput,
  $hasRepeatOffsets,
  $direction,

  theme: {
    indents,
  },
}) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  minHeight: (
    $direction === 'horizontal'
    && $isInput
  ) ? 40 : undefined,
  paddingLeft: $hasRepeatOffsets ? indents.INDENT_2 : 0,
}));

export const StyledLabel = styled.div<{
  $isHorizontal: boolean;
}>(({
  $isHorizontal,

  theme: {
    fieldGroup,
    colors,
    fonts,
  },
}) => ({
  ...fonts[fieldGroup.LABEL_FONT],
  paddingTop: 4,
  paddingBottom: 4,
  flex: $isHorizontal ? 1 : undefined,
  color: colors[fieldGroup.COLOR],
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  textAlign: $isHorizontal ? 'right' : undefined,
}));

const StyledHintWrapper = styled.div({
  flex: '0 0 auto',
  display: 'flex',
  paddingLeft: 4,
});

export const StyledRequiredStar = styled.div<{
  $isInput: boolean;
  $isHorizontal: boolean;
}>(({
  $isInput,
  $isHorizontal,

  theme: {
    colors,
    fonts,
  },
}) => {
  if ($isHorizontal) {
    return {
      ...fonts.DEFAULT,
      position: 'absolute',
      color: colors.ERROR_DEFAULT,
      top: $isInput ? 8 : 0,
      right: -20,
    };
  }

  return {
    ...fonts.DEFAULT,
    color: colors.ERROR_DEFAULT,

    '&::before': {
      content: ' ',
    },
  };
});

export const StyledUnit = styled.div<{
  $isInput: boolean;
}>(({
  $isInput,

  theme: {
    colors,
    fonts,
  },
}) => ({
  ...fonts.DEFAULT,
  color: colors.NEUTRAL_DEFAULT,
  marginTop: $isInput ? 8 : 0,
  marginLeft: -24,
}));

export const StyledContentWrapper = styled.div<{
  $hasRightOffset: boolean;
}>(({
  $hasRightOffset,

  theme: {
    indents,
  },
}) => ({
  paddingRight: $hasRightOffset ? indents.INDENT_2 : 0,
}));

const StyledVerticalWrapper = styled.div(({
  theme: {
    fieldGroup,
  },
}) => ({
  display: 'grid',
  gap: fieldGroup.HORIZONTAL_GAP,
}));

export type FieldGroupDirectionType = 'horizontal' | 'vertical';

export type FieldGroupProps = {
  /**
   * Если true, после заголовка выводится двоеточие
   */
  colon?: boolean;
  /**
   * Направление лейаута поля
   */
  direction?: FieldGroupDirectionType;
  /**
   * Если true, поле выводится без заголовка
   */
  onlyField?: boolean;

  /**
   * Заголовок поля
   */
  label?: string;
  /**
   * Обязательность поля
   */
  required?: boolean;
  /**
   * Пропорциональная ширина поля и контейнера для единиц измерения
   */
  isCellsPercentage?: boolean;
  /**
   * Задать отступ заголовка, чтобы он был в одну линию с текстом поля ввода
   */
  isInput?: boolean;
  /**
   * Вывод в режиме просмотра (влияет на расстояние между полями)
   */
  isShow?: boolean;
  /**
   * Вывод внутри `Repeat.Block`
   */
  hasRepeatOffsets?: boolean;

  /**
   * Подсказка, выводящаяся в тултипе рядом с заголовком
   */
  hint?: ReactNode;
  /**
   * Единицы измерения, выводится справа от поля
   */
  unit?: ReactNode;

  /**
   * Подсказки поля
   */
  help?: string[];
  /**
   * Ошибки поля
   */
  errors?: string[];
  /**
   * Предупреждения поля
   */
  warnings?: string[];
  /**
   * Менять ли фон поля в случае ошибки или предупреждения
   */
  showBackground?: boolean;
  /**
   * Количество колонок заголовка
   */
  labelColsXs?: CellValue;
  /**
   * Количество колонок контента
   */
  fieldColsXs?: CellValue;
  /**
   * Количество колонок единиц измерения
   */
  unitColsXs?: CellValue;

  /**
   * Контент поля
   */
  children?: ReactNode;
};

export function FieldGroup({
  onlyField = false,

  label = undefined,
  colon = true,
  required = false,
  isCellsPercentage = false,
  isInput = false,
  isShow = false,
  hasRepeatOffsets = false,

  hint = undefined,
  unit = undefined,

  help = undefined,
  errors = undefined,
  warnings = undefined,
  showBackground = false,
  direction = 'horizontal',

  labelColsXs = 3,
  fieldColsXs = 5,
  unitColsXs = 1,

  children = undefined,
}: FieldGroupProps): ReactElement {
  const theme = useTheme();

  const isHorizontal = direction === 'horizontal';

  const renderedLabel = (
    <StyledLabelBlock
      $isInput={isInput}
      $hasRepeatOffsets={hasRepeatOffsets}
      $direction={direction}
    >
      {
        label && (
          <StyledLabel
            $isHorizontal={isHorizontal}
          >
            {label}
            {colon && ':'}
          </StyledLabel>
        )
      }

      {
        hint && (
          <StyledHintWrapper>
            <Hint
              tooltip={hint}
            />
          </StyledHintWrapper>
        )
      }

      {
        required && (
          <StyledRequiredStar
            $isInput={isInput}
            $isHorizontal={isHorizontal}
          >
            *
          </StyledRequiredStar>
        )
      }
    </StyledLabelBlock>
  );

  const renderedContent = (
    <StyledContentWrapper
      $hasRightOffset={hasRepeatOffsets}
    >
      <FieldWrapper
        help={help}
        errors={errors}
        warnings={warnings}
        showBackground={showBackground}
      >
        {children}
      </FieldWrapper>
    </StyledContentWrapper>
  );

  const renderedUnit = (
    <StyledUnit
      $isInput={isInput}
    >
      {unit}
    </StyledUnit>
  );

  return (
    <StyledWrapper
      $isShow={isShow}
      $hasRepeatOffsets={hasRepeatOffsets}
    >
      {isHorizontal ? (
        <Row $rowGap={theme.fieldGroup.HORIZONTAL_GAP}>
          {
            !onlyField && (
              <Cell isPercenage={isCellsPercentage} xs={labelColsXs}>
                {renderedLabel}
              </Cell>
            )
          }

          <Cell isPercenage={isCellsPercentage} xs={fieldColsXs}>
            {renderedContent}
          </Cell>

          {
            unit && (
              <Cell isPercenage={isCellsPercentage} xs={unitColsXs}>
                {renderedUnit}
              </Cell>
            )
          }
        </Row>
      ) : (
        <StyledVerticalWrapper>
          {renderedLabel}

          <Row>
            <Cell isPercenage={isCellsPercentage} xs={fieldColsXs}>
              {renderedContent}
            </Cell>

            {
              unit && (
                <Cell isPercenage={isCellsPercentage} xs={unitColsXs}>
                  {renderedUnit}
                </Cell>
              )
            }
          </Row>
        </StyledVerticalWrapper>
      )}
    </StyledWrapper>
  );
}
